import GokuDataGrid from '@/components/GokuDataGrid'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import { CreateOperationExpenseModalProps } from '@/modals/OperationExpenseModals/CreateModal'
import { UpdateOperationExpenseModalProps } from '@/modals/OperationExpenseModals/UpdateModal'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { IAction } from 'vnetwork-ui/dist/components/GokuDataGrid/Actions'
import { deleteOperationExpense, listOperationExpenses } from '../../services'
import { OperationExpense, OperationExpenseListParams } from '../../types'
import { columns } from '../columns'

interface OperationExpensesListProps {
    filters?: OperationExpenseListParams
}

const OperationExpensesList: React.FC<OperationExpensesListProps> = ({
    filters,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    // Fetch expenses

    // Delete mutation
    const { mutateAsync: deleteItemMutation } = useMutationWithHandler(
        {
            mutationFn: deleteOperationExpense,
        },
        {
            reactQueryKeys: [QUERY_KEYS.REPORTS_BI_OPERATION_EXPENSES],
        }
    )

    const handleDeleteClick = (item: OperationExpense) => {
        setConfirmation({
            open: true,
            title: t('confirm_delete'),
            description: t('confirm_delete_operation_expense_message'),
            onConfirm: () => handleDeleteItem(item.id),
        })
    }

    const handleDeleteItem = async (id: number) => {
        try {
            await deleteItemMutation(id)
        } catch (error) {
            console.error('Failed to delete operation expense:', error)
        }
    }

    const handleOpenCreate = () => {
        setModal<CreateOperationExpenseModalProps>(
            'OperationExpenseModals/CreateModal/index'
        )
    }

    const handleOpenEdit = (item: OperationExpense) => {
        setModal<UpdateOperationExpenseModalProps>(
            'OperationExpenseModals/UpdateModal/index',
            {
                expense: item,
            }
        )
    }

    // DataGrid actions
    const dataGridActions: IAction<OperationExpense>[] = [
        {
            onClick: handleOpenEdit,
            title: t('edit'),
            color: 'primary',
            icon: <EditIcons />,
        },
        {
            onClick: handleDeleteClick,
            title: t('delete'),
            color: 'error',
            icon: <DeleteIcons />,
        },
    ]

    return (
        <div className="space-y-2">
            <GokuDataGrid
                apiCall={async (_params) => {
                    const data = await listOperationExpenses(filters)
                    return {
                        results: data,
                        total: data.length,
                    }
                }}
                columns={columns(t)}
                actions={dataGridActions}
                dataGridProps={{
                    getRowId: (row) => row.id,
                    hideFooter: true,
                    hideFooterPagination: true,
                }}
                options={{
                    pageSize: 50,
                    title: (
                        <div className="flex justify-end gap-2 p-2">
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleOpenCreate}
                                startIcon={<AddIcons />}
                            >
                                {t('create')}
                            </Button>
                        </div>
                    ),
                    showSearch: false,
                }}
                reactQueryKey={
                    QUERY_KEYS.REPORTS_BI_OPERATION_EXPENSES +
                    JSON.stringify(filters)
                }
            />
        </div>
    )
}

export default OperationExpensesList
